import type { Row } from "./api";
import {
  friendlyFieldLabel,
  hasTechnicalDependency,
  resolveIntakeSchema,
  validateIntakeValue,
} from "./intake-values";
import {
  deriveTaxYearAge,
  isTaxObject,
  normalizeTaxAge,
  taxAnswerView,
  taxDesigneeDetails,
  taxNullableSchema,
  taxVisibleObjectSchema,
} from "./tax-field-values";

export interface FieldIssue {
  id: string;
  targetId: string;
  path: string[];
  sectionId: string;
  sectionLabel: string;
  fieldLabel: string;
  kind: "required" | "invalid";
  reason: string;
  code?:
    | "FIELD_REQUIRED"
    | "SOURCE_INVALID"
    | "FORMAT_INVALID"
    | "ANSWER_CONFLICT"
    | "ANSWER_UNKNOWN";
}
export interface FieldValidationContext {
  sectionId: string;
  sectionLabel: string;
  rootLabel: string;
  rootId: string;
  definitions?: Row;
  presentation?: "natural" | "legacy";
}
const owns = (value: object, key: string) =>
  Object.prototype.hasOwnProperty.call(value, key);
/** Length-framed segments avoid collisions between field names, array indices and their separators. */
export function fieldTargetId(rootId: string, path: readonly string[]): string {
  const segment = (value: string) =>
    `${value.length}-${Array.from({ length: value.length }, (_, index) => value.charCodeAt(index).toString(16).padStart(4, "0")).join("")}`;
  return `tax-field-${[rootId, ...path].map(segment).join("--")}`;
}

/** Field-level draft review. No issue is an admission, evidence or tax-calculation result. */
export function validateTaxFields(
  schema: Row,
  value: unknown,
  context: FieldValidationContext,
): FieldIssue[] {
  const issues: FieldIssue[] = [];
  const add = (
    path: string[],
    label: string,
    kind: FieldIssue["kind"],
    reason: string,
    targetPath = path,
    code: FieldIssue["code"] = kind === "required"
      ? "FIELD_REQUIRED"
      : "SOURCE_INVALID",
  ) => {
    const id = `${fieldTargetId(context.rootId, path)}--${kind}`;
    if (!issues.some((issue) => issue.id === id))
      issues.push({
        id,
        targetId: fieldTargetId(context.rootId, targetPath),
        path: [...path],
        sectionId: context.sectionId,
        sectionLabel: context.sectionLabel,
        fieldLabel: label,
        kind,
        reason,
        code,
      });
  };
  const humanReason = (reason: string) => reason.replace(/^[^:]+:\s*/, "");
  const preciseReason = (
    field: Row,
    item: unknown,
    fallback: string,
  ): string => {
    if (field.type === "boolean" && typeof item !== "boolean")
      return "Choose Yes or No using the available choices; text is not a Yes/No answer.";
    if (field.type === "integer" && !Number.isSafeInteger(item))
      return "Enter an exact whole number, without decimals, between -9007199254740991 and 9007199254740991.";
    if (
      field.type === "number" &&
      (typeof item !== "number" || !Number.isFinite(item))
    )
      return "Enter a finite number without rounding or unsupported characters.";
    if (typeof item === "number") {
      if (field.minimum !== undefined && item < field.minimum)
        return `Enter a number greater than or equal to ${field.minimum}.`;
      if (field.maximum !== undefined && item > field.maximum)
        return `Enter a number less than or equal to ${field.maximum}.`;
      if (
        field.exclusiveMinimum !== undefined &&
        item <= field.exclusiveMinimum
      )
        return `Enter a number greater than ${field.exclusiveMinimum}.`;
      if (
        field.exclusiveMaximum !== undefined &&
        item >= field.exclusiveMaximum
      )
        return `Enter a number less than ${field.exclusiveMaximum}.`;
      if (
        field.multipleOf !== undefined &&
        humanReason(fallback).includes("increment")
      )
        return `Enter a number in increments of ${field.multipleOf}.`;
    }
    if (typeof item === "string") {
      if (field.minLength !== undefined && [...item].length < field.minLength)
        return `Enter at least ${field.minLength} character${field.minLength === 1 ? "" : "s"}.`;
      if (field.maxLength !== undefined && [...item].length > field.maxLength)
        return `Use no more than ${field.maxLength} characters.`;
      if (field.pattern) {
        let matches = true;
        try {
          matches = new RegExp(field.pattern, "u").test(item);
        } catch {
          return "The required field format is unavailable.";
        }
        if (!matches) {
          if (
            field.pattern.includes("[0-9]{2}") &&
            field.pattern.includes("\\.")
          )
            return "Enter an exact amount with two decimal places and no commas or currency symbol, for example 1234.56.";
          if (field.pattern.includes("{5}") && field.pattern.includes("{4}"))
            return "Enter a ZIP code as 12345 or 12345-6789.";
          const digits = /^\^\[0-9\]\{(\d+)\}\$$/.exec(field.pattern);
          if (digits)
            return `Enter exactly ${digits[1]} digits, without spaces or punctuation.`;
          const letters = /^\^\[A-Z\]\{(\d+)\}\$$/.exec(field.pattern);
          if (letters) return `Enter exactly ${letters[1]} uppercase letters.`;
          if (field.pattern === "^\\+?[1-9][0-9]{7,14}$")
            return "Enter 8 to 15 digits starting with a nonzero digit, with an optional leading + and no spaces.";
        }
      }
      if (field.format === "date")
        return "Enter a valid calendar date in YYYY-MM-DD format.";
      if (field.format === "date-time")
        return "Enter a valid date and time with an explicit time-zone offset, for example 2025-12-31T17:00:00-08:00.";
      if (field.format === "email")
        return "Enter an email address such as name@example.com.";
    }
    return humanReason(fallback);
  };
  const visit = (
    shape: Row,
    item: unknown,
    path: string[],
    label: string,
    required: boolean,
    depth: number,
    parent?: Row,
  ) => {
    if (depth > 24 || hasTechnicalDependency(shape, context.definitions)) {
      add(
        path,
        label,
        "invalid",
        "This information needs a connected preparation or review step.",
      );
      return;
    }
    const resolved = resolveIntakeSchema(shape, context.definitions);
    if (!resolved) {
      add(
        path,
        label,
        "invalid",
        "The required field definition is not available.",
      );
      return;
    }
    if (resolved["x-intake-stateful"]) {
      const answer = taxAnswerView(item);
      if (answer.replacementRequired) {
        const conflict = isTaxObject(item) && item.state === "CONFLICT";
        add(
          path,
          label,
          conflict ? "invalid" : "required",
          conflict
            ? "The saved information conflicts. Replace it when the correct information is available."
            : "An answer is still needed.",
          path,
          conflict ? "ANSWER_CONFLICT" : "ANSWER_UNKNOWN",
        );
        return;
      }
      visit(
        resolved["x-intake-stateful"].valueSchema,
        answer.value,
        path,
        label,
        required,
        depth + 1,
      );
      return;
    }
    const nullable = taxNullableSchema(resolved, context.definitions);
    if (!nullable) {
      add(path, label, "invalid", "This entry needs a supported input format.");
      return;
    }
    const field = nullable.schema;
    if (item === undefined && !required) return;
    if (
      item === null &&
      nullable.nullable &&
      validateIntakeValue(item, resolved, context.definitions).valid
    )
      return;
    if (item === "UNKNOWN" && field.enum?.includes("UNKNOWN")) {
      add(
        path,
        label,
        "required",
        "An answer is still needed. Choose an answer when the information is available.",
        path,
        "ANSWER_UNKNOWN",
      );
      return;
    }
    if (item === "CONFLICT" && field.enum?.includes("CONFLICT")) {
      add(
        path,
        label,
        "invalid",
        "The saved information conflicts. Replace it when the correct information is available.",
        path,
        "ANSWER_CONFLICT",
      );
      return;
    }
    if (
      field.type === "object" &&
      (field.properties || field.allOf || field.if)
    ) {
      if (item === undefined && nullable.nullable) {
        add(
          path,
          label,
          "required",
          "Add this information or explicitly mark it as not provided.",
        );
        return;
      }
      if (item !== undefined && !isTaxObject(item)) {
        add(
          path,
          label,
          "invalid",
          "Enter the required details using the fields below.",
        );
        return;
      }
      const hasAge = Boolean(
        field.properties?.date_of_birth && field.properties?.age_for_2025,
      );
      const current = (hasAge ? normalizeTaxAge(item) : item) as
        Row | undefined;
      const visible = taxVisibleObjectSchema(
        field,
        current,
        context.definitions,
      );
      if (!visible) {
        add(path, label, "invalid", "The required fields are not available.");
        return;
      }
      const before = issues.length;
      const designee =
        context.presentation === "legacy"
          ? {
              hideDetails: false,
              missingNullableFields: [],
              hasRetainedDetails: false,
            }
          : taxDesigneeDetails(visible, current, context.definitions);
      for (const [key, child] of Object.entries(visible.properties)) {
        const childSchema = child as Row,
          childLabel =
            childSchema["x-tax-label"] ??
            childSchema["x-intake-label"] ??
            friendlyFieldLabel(key);
        const childValue = current?.[key],
          childPath = [...path, key];
        if (hasAge && key === "age_for_2025") continue;
        if (
          designee.hideDetails &&
          ["designee_name", "designee_phone", "designee_pin"].includes(key)
        ) {
          if (designee.missingNullableFields.includes(key))
            add(
              childPath,
              childLabel,
              "required",
              "Confirm that there are no designee details to provide.",
              [...path, "@designee-confirmation"],
            );
          else if (
            childValue !== undefined &&
            !validateIntakeValue(childValue, childSchema, context.definitions)
              .valid
          )
            add(
              childPath,
              childLabel,
              "invalid",
              "A retained designee detail has an invalid format. Choose Yes to inspect and correct it.",
              path,
            );
          continue;
        }
        if (
          hasAge &&
          key === "date_of_birth" &&
          childValue !== undefined &&
          childValue !== "" &&
          deriveTaxYearAge(childValue) === undefined
        ) {
          add(
            childPath,
            childLabel,
            "invalid",
            "Enter a valid birth date on or before December 31, 2025, giving a tax-year age from 0 to 150.",
          );
          continue;
        }
        visit(
          childSchema,
          childValue,
          childPath,
          childLabel,
          visible.required.includes(key),
          depth + 1,
          current,
        );
      }
      if (
        isTaxObject(current) &&
        field.additionalProperties === false &&
        Object.keys(current).some((key) => !owns(visible.properties, key))
      )
        add(
          path,
          label,
          "invalid",
          "This entry contains a field that is not supported by the current form.",
        );
      if (isTaxObject(current) && issues.length === before) {
        const checked = validateIntakeValue(
          current,
          field,
          context.definitions,
        );
        if (!checked.valid)
          add(path, label, "invalid", humanReason(checked.errors[0]));
      }
      return;
    }
    if (
      item === undefined ||
      (typeof item === "string" &&
        item.trim() === "" &&
        (required || (field.minLength ?? 0) > 0))
    ) {
      add(
        path,
        label,
        "required",
        nullable.nullable
          ? "Enter a value or explicitly mark it as not provided."
          : field.type === "boolean"
            ? "Choose Yes or No."
            : Array.isArray(field.enum)
              ? field.enum.includes("PRESENT") && field.enum.includes("ABSENT")
                ? "Choose Yes or No. Select Not sure if you need to follow up."
                : "Choose one of the available answers."
              : field.type === "array"
                ? "Add an item or explicitly confirm there are no items."
                : "Enter a value for this field.",
      );
      return;
    }
    if (field.type === "array") {
      if (!Array.isArray(item)) {
        add(path, label, "invalid", "Enter the items using the fields below.");
        return;
      }
      if ((field.minItems ?? 0) > item.length)
        add(
          path,
          label,
          "required",
          `Add at least ${field.minItems} item${field.minItems === 1 ? "" : "s"}.`,
        );
      const before = issues.length;
      if (isTaxObject(field.items))
        item.forEach((child, index) =>
          visit(
            field.items,
            child,
            [...path, String(index)],
            `${field["x-tax-item-label"] ?? "Item"} ${index + 1}`,
            true,
            depth + 1,
          ),
        );
      if (issues.length === before) {
        const checked = validateIntakeValue(item, field, context.definitions);
        if (!checked.valid && !(field.minItems > item.length))
          add(path, label, "invalid", humanReason(checked.errors[0]));
      }
      return;
    }
    const checked = validateIntakeValue(item, field, context.definitions);
    if (!checked.valid) {
      add(
        path,
        label,
        "invalid",
        preciseReason(field, item, checked.errors[0]),
      );
      return;
    }
    // Explicit presentation checks for ordinary contact fields. They do not mutate the source schemas or supplied text.
    if (typeof item === "string" && item.length > 0) {
      const key = path.at(-1) ?? "",
        normalizedLabel = label.toLocaleLowerCase("en");
      if (
        (/(^|_)email$/.test(key) ||
          (!path.length && /email/.test(normalizedLabel))) &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item)
      )
        add(
          path,
          label,
          "invalid",
          "Enter an email address such as name@example.com.",
          path,
          "FORMAT_INVALID",
        );
      if (
        /(^|_)phone$/.test(key) ||
        (!path.length && /phone/.test(normalizedLabel))
      ) {
        const parts = item.match(/^(.*?)(?:\s*(?:ext\.?|x|#)\s*(\d{1,8}))?$/i);
        const base = parts?.[1] ?? "",
          digits = base.replace(/\D/g, "");
        if (
          !/^\+?[\d\s().-]+$/.test(base) ||
          digits.length < 7 ||
          digits.length > 15
        )
          add(
            path,
            label,
            "invalid",
            "Enter a phone number with 7 to 15 digits. Spaces, +, parentheses, hyphens and an optional extension are allowed.",
            path,
            "FORMAT_INVALID",
          );
      }
      const foreignCountry =
        typeof parent?.foreign_country === "string"
          ? parent.foreign_country.trim()
          : "";
      const foreignAddress =
        foreignCountry !== "" &&
        !/^(US|USA|United States|United States of America)$/i.test(
          foreignCountry,
        );
      if (
        (key === "zip" ||
          key === "zip_code" ||
          (!path.length && /\bzip\b/.test(normalizedLabel))) &&
        !foreignAddress &&
        !/^\d{5}(?:-\d{4})?$/.test(item)
      )
        add(
          path,
          label,
          "invalid",
          "Enter a ZIP code as 12345 or 12345-6789.",
          path,
          "FORMAT_INVALID",
        );
    }
  };
  visit(schema, value, [], context.rootLabel, true, 0);
  return issues;
}
